import React, { useState, useEffect } from 'react';
import { signIn, signUp, resetPassword, checkAllowedUser, signInWithGoogle, subscribeGoogleAccessDenied } from '../firebase';
import { GOOGLE_SIGNIN_ENABLED } from '../config/features';
import PulseLine from '../components/ui/PulseLine';

// Login / registration / password reset (redesign 2026-10-20, UI only — every handler below is unchanged).
// Colors come from the design tokens (tailwind.config.js; Graphite + Lime since 2026-10-27 — the primary button is the
// lime CTA with #111 text). One documented exception:
//   • the Google button — Google's branding guidelines require #FFFFFF fill, #747775 1px stroke, #1F1F1F text.
// Official multicolor "G" — the PNGs of Google's sign-in branding bundle (signin-assets.zip, "Light, Show text=No,
// Square"), cropped pixel-for-pixel to the G's own 20×20 square (no resampling / recolouring). PNG rather than the
// bundle's SVG, whose gradient is drawn with <foreignObject> (unreliable in some WebKit / PWA renderers).
const GOOGLE_G = '/icons/google-g@2x.png';
const GOOGLE_G_SRCSET = '/icons/google-g@2x.png 2x, /icons/google-g@3x.png 3x, /icons/google-g@4x.png 4x';
const BENEFITS = ['תכנית אימונים מותאמת', 'ניתוח כל ריצה', 'מאמן AI זמין תמיד'];

const FOCUS = 'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
const INPUT = 'block h-[50px] w-full rounded-card border border-border bg-surface-2 md:bg-bg px-4 text-[16px] text-text '
  + 'placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent transition-colors';
const LABEL = 'block text-[13px] font-medium text-text-body';
const PRIMARY_BTN = `flex h-[52px] w-full items-center justify-center gap-2 rounded-full btn-primary text-[17px] `
  + `transition ${FOCUS}`;
const LINK_BTN = `rounded-badge text-[13px] font-medium text-text-body underline underline-offset-2 ${FOCUS}`;

function AuthPage({ onAuthSuccess, inviteCode }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const busy = loading || googleLoading;
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Why a Google session was not let in — also when another tab (or a reload) ran the check.
  useEffect(() => (GOOGLE_SIGNIN_ENABLED ? subscribeGoogleAccessDenied(setError) : undefined), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('נא למלא את כל השדות');
      setLoading(false);
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('הסיסמאות לא תואמות');
      setLoading(false);
      return;
    }

    if (!isLogin && password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (isLogin) {
        result = await signIn(email, password);
      } else {
        const { allowed } = await checkAllowedUser(email);
        if (!allowed) {
          setError('האפליקציה בשלב פיילוט סגור.\nלפרטים: support@ritmoiq.com');
          setLoading(false);
          return;
        }
        result = await signUp(email, password);
      }

      if (result.success) {
        onAuthSuccess(result.user);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('שגיאה לא צפויה. נסה שוב');
    } finally {
      setLoading(false);
    }
  };

  // "המשך עם Google" (GOOGLE_SIGNIN_ENABLED). The access check + sign-out on denial live in signInWithGoogle;
  // here only the result is shown. Cancelling the popup shows nothing.
  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        onAuthSuccess(result.user);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError('שגיאה לא צפויה. נסה שוב');
    } finally {
      setGoogleLoading(false);
    }
  };

  const openResetPassword = () => {
    setError('');
    setShowResetPassword(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('נא להזין כתובת אימייל');
      setLoading(false);
      return;
    }

    const result = await resetPassword(email);
    
    if (result.success) {
      setResetSent(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const backToLogin = () => {
    setShowResetPassword(false);
    setResetSent(false);
  };

  // Reset Password View
  if (showResetPassword) {
    return (
      <AuthShell>
        {resetSent ? (
          <section className="flex flex-col gap-3 text-center" aria-live="polite">
            <h2 className="text-[20px] font-bold text-text">בדוק את תיבת המייל</h2>
            {/* neutral: never says whether an account exists for this email */}
            <p className="text-[15px] leading-relaxed text-text-body">
              אם קיים חשבון RitmoIQ עם האימייל הזה, שלחנו אליו קישור לקביעת סיסמה חדשה.
            </p>
            <p className="text-[13px] text-text-muted">לא הגיע? כדאי לבדוק גם בתיקיית הספאם.</p>
            <button type="button" onClick={backToLogin}
                    className={`${PRIMARY_BTN} mt-2`}>
              חזרה להתחברות
            </button>
          </section>
        ) : (
          <form onSubmit={handleResetPassword} className="flex flex-col gap-[14px]" noValidate>
            <div className="text-center">
              <h2 className="text-[20px] font-bold text-text">איפוס סיסמה</h2>
              <p className="mt-1 text-[14px] text-text-muted">הזן את כתובת האימייל שלך ונשלח לך קישור לאיפוס</p>
            </div>

            <ErrorBox message={error} />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="auth-reset-email" className={LABEL}>אימייל</label>
              <input
                id="auth-reset-email"
                type="email"
                dir="ltr"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${INPUT} text-left`}
                placeholder="name@example.com"
              />
            </div>

            <button type="submit" disabled={loading} className={PRIMARY_BTN}>
              {loading ? <><Spinner /><span>שולח...</span></> : 'שלח קישור איפוס'}
            </button>

            <button type="button" onClick={() => setShowResetPassword(false)}
                    className={`mx-auto px-3 py-2 text-[14px] text-text-muted hover:text-text ${FOCUS} rounded-badge`}>
              חזרה להתחברות
            </button>
          </form>
        )}
      </AuthShell>
    );
  }

  // Login / Register tabs: arrow keys move between the two tabs (WAI-ARIA tabs pattern).
  const onTabsKeyDown = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const toLogin = e.key === 'Home' ? true : e.key === 'End' ? false : !isLogin;
      setIsLogin(toLogin);
      document.getElementById(toLogin ? 'auth-tab-login' : 'auth-tab-register')?.focus();
    }
  };
  const tabCls = (active) => `h-10 rounded-control text-[15px] transition-colors ${FOCUS} `
    + (active ? 'bg-accent text-on-accent font-bold' : 'bg-transparent text-text-muted font-medium hover:text-text-body');

  // Main Login/Signup View
  return (
    <AuthShell subtitle={isLogin ? 'התחבר כדי להמשיך לתכנית שלך' : 'הצטרף לפיילוט וקבל תכנית אישית'}>
      {/* every item carries its own cyan dot, so wrapping never leaves a dangling separator */}
      <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[14px] text-text-muted">
        {BENEFITS.map((b) => (
          <li key={b} className="flex items-center gap-1.5 whitespace-nowrap">
            <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {/* Invite Banner - if coming from coach invite */}
      {inviteCode && (
        <div className="rounded-card border border-border bg-accent-soft px-4 py-3 text-center">
          <p className="text-[14px] font-medium text-text">הוזמנת להצטרף לקבוצת ריצה!</p>
          <p className="text-[13px] text-text-body">התחבר או הירשם כדי להצטרף למאמן</p>
        </div>
      )}

      {/* Google (GOOGLE_SIGNIN_ENABLED) — one button for both tabs; sign-in vs sign-up is decided by the existing
          access check in signInWithGoogle */}
      {GOOGLE_SIGNIN_ENABLED && (
        <div className="flex flex-col gap-[14px]">
          <button
            type="button"
            onClick={handleGoogle}
            disabled={busy}
            className={`flex h-[50px] w-full items-center justify-center gap-[10px] rounded-card border border-[#747775] bg-white px-3 text-[15px] font-medium text-[#1F1F1F] transition hover:border-[#1F1F1F] disabled:opacity-60 ${FOCUS}`}
          >
            {googleLoading ? (
              <><Spinner dark /><span>מתחבר עם Google...</span></>
            ) : (
              <>
                <img src={GOOGLE_G} srcSet={GOOGLE_G_SRCSET} alt="" aria-hidden="true" width="20" height="20"
                     className="block h-5 w-5 shrink-0" />
                <span>המשך עם Google</span>
              </>
            )}
          </button>
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-divider" />
            <span className="text-[13px] text-text-muted">או עם אימייל</span>
            <span className="h-px flex-1 bg-divider" />
          </div>
        </div>
      )}

      <div role="tablist" aria-label="התחברות או הרשמה" onKeyDown={onTabsKeyDown}
           className="grid grid-cols-2 gap-1 rounded-card bg-surface-2 md:bg-bg p-1">
        <button type="button" role="tab" id="auth-tab-login" aria-controls="auth-panel" aria-selected={isLogin}
                tabIndex={isLogin ? 0 : -1} onClick={() => setIsLogin(true)} className={tabCls(isLogin)}>
          התחברות
        </button>
        <button type="button" role="tab" id="auth-tab-register" aria-controls="auth-panel" aria-selected={!isLogin}
                tabIndex={isLogin ? -1 : 0} onClick={() => setIsLogin(false)} className={tabCls(!isLogin)}>
          הרשמה
        </button>
      </div>

      <form id="auth-panel" role="tabpanel" aria-labelledby={isLogin ? 'auth-tab-login' : 'auth-tab-register'}
            onSubmit={handleSubmit} className="flex flex-col gap-[14px]" noValidate>
        <ErrorBox message={error} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="auth-email" className={LABEL}>אימייל</label>
          <input
            id="auth-email"
            type="email"
            dir="ltr"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${INPUT} text-left`}
            placeholder="name@example.com"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="auth-password" className={LABEL}>סיסמה</label>
            {isLogin && (
              <button type="button" onClick={openResetPassword} className={`${LINK_BTN} -my-1 px-1 py-1`}>
                שכחת סיסמה?
              </button>
            )}
          </div>
          <PasswordInput
            id="auth-password"
            value={password}
            onChange={setPassword}
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            visible={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
          />
        </div>

        {!isLogin && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="auth-password-confirm" className={LABEL}>אימות סיסמה</label>
            <PasswordInput
              id="auth-password-confirm"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              visible={showPassword}
            />
          </div>
        )}

        <button type="submit" disabled={busy} className={`${PRIMARY_BTN} mt-1`}>
          {loading ? <><Spinner /><span>מתחבר...</span></> : (isLogin ? 'התחבר' : 'הירשם')}
        </button>
      </form>

      {/* Permanent help (GOOGLE_SIGNIN_ENABLED): an account that moved to Google keeps working with Google, and
          "forgot password" sets a new RitmoIQ password on the SAME account. Says nothing about any specific email. */}
      {GOOGLE_SIGNIN_ENABLED && isLogin && (
        <p className="text-center text-[13px] leading-relaxed text-text-muted" data-testid="auth-google-help">
          התחברת בעבר עם Google? אפשר להמשיך עם Google או{' '}
          <button type="button" onClick={openResetPassword} className={`${LINK_BTN} underline`}>
            לאפס את סיסמת RitmoIQ
          </button>.
        </p>
      )}
    </AuthShell>
  );
}

// Shared frame: tokens' base background; on desktop a 480px surface; header = app icon → "RitmoIQ" → one subtitle,
// with the brand pulse line faintly behind the logo only (never behind the form).
function AuthShell({ subtitle, children }) {
  return (
    <div dir="rtl" className="min-h-[100dvh] bg-bg md:flex md:items-center md:justify-center md:px-6 md:py-6">
      <div className="w-full md:max-w-[480px] md:rounded-[24px] md:border md:border-border md:bg-surface-2 md:shadow-card">
        <main className="mx-auto flex w-full max-w-[400px] flex-col gap-[18px] px-6 pb-6 pt-8">
          <header className="relative text-center">
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[15px] opacity-[0.22]">
              <PulseLine />
            </div>
            <img src="/icons/ritmoiq-r.png" alt="" aria-hidden="true"
                 className="relative mx-auto h-14 w-14 rounded-[14px] object-cover" />
            <h1 className="relative mt-3 text-[26px] font-extrabold leading-tight text-text">RitmoIQ</h1>
            {subtitle && <p className="relative mt-1 text-[15px] text-text-muted">{subtitle}</p>}
          </header>

          {children}

          <footer className="pt-1 text-center text-[12px] text-text-muted">
            <p>פיילוט סגור · חינם לחלוצים</p>
            <p className="mt-1">
              <a href="/terms" className={`${FOCUS} rounded-badge hover:text-text-body hover:underline underline-offset-2`}>תנאי שימוש</a>
              <span aria-hidden="true"> · </span>
              <a href="/privacy" className={`${FOCUS} rounded-badge hover:text-text-body hover:underline underline-offset-2`}>מדיניות פרטיות</a>
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

// Neutral inline error (role="alert"), above the form fields.
function ErrorBox({ message }) {
  if (!message) return null;
  return (
    <div role="alert" className="flex items-start gap-2 rounded-card border border-quality bg-quality-soft px-3 py-2.5">
      <svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18" className="mt-0.5 shrink-0 text-quality">
        <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 5.75v5.5M10 13.9v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <p className="whitespace-pre-line text-[14px] leading-snug text-text">{message}</p>
    </div>
  );
}

// Password field; the eye button (44×44) shows / hides the text. Passwords are LTR like the email.
function PasswordInput({ id, value, onChange, autoComplete, visible, onToggle }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        dir="ltr"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT} text-left ${onToggle ? 'pl-12' : ''}`}
      />
      {onToggle && (
        <button type="button" onClick={onToggle} aria-label={visible ? 'הסתרת סיסמה' : 'הצגת סיסמה'}
                className={`absolute left-[3px] top-[3px] flex h-11 w-11 items-center justify-center rounded-control text-text-muted hover:text-text ${FOCUS}`}>
          <EyeIcon off={visible} />
        </button>
      )}
    </div>
  );
}

function EyeIcon({ off }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M4 4l16 16" />}
    </svg>
  );
}

function Spinner({ dark = false }) {
  return (
    <span aria-hidden="true"
          className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent ${dark ? 'border-[#1F1F1F]' : 'border-on-accent'}`} />
  );
}

export default AuthPage;
